import { PieceBackfillRunStatus } from "../../generated/graphql";

/** How often a live run is re-read while it is still in flight. */
export const POLL_INTERVAL_MS = 1500;

/**
 * Stop polling after this many consecutive failed reads. The run row is
 * persistent, so a read that keeps failing means the session lost the backend
 * rather than that the run vanished; without this guard the query would retry
 * forever behind the operator's back.
 */
export const MAX_MISSED_POLLS = 5;

/** Statuses a run never leaves. */
const TERMINAL_STATUSES: ReadonlySet<PieceBackfillRunStatus> = new Set([
  PieceBackfillRunStatus.Completed,
  PieceBackfillRunStatus.Failed,
]);

/**
 * Whether a run has finished and will never change again.
 *
 * @param status The run's status.
 * @returns True for COMPLETED and FAILED.
 */
export const isTerminalRunStatus = (
  status: PieceBackfillRunStatus,
): boolean => {
  return TERMINAL_STATUSES.has(status);
};

/**
 * Whether the worker still owes the run an outcome, which is what the progress
 * view keys off.
 *
 * @param status The run's status.
 * @returns True for PENDING and RUNNING.
 */
export const isRunInFlight = (status: PieceBackfillRunStatus): boolean => {
  return !isTerminalRunStatus(status);
};

/**
 * Whether a run of consecutive failed polls has gone on long enough to give up
 * on.
 *
 * @param consecutiveFailures How many polls in a row have failed.
 * @returns True once the tolerance is exhausted.
 */
export const shouldStopPollingAfterFailures = (
  consecutiveFailures: number,
): boolean => {
  return consecutiveFailures >= MAX_MISSED_POLLS;
};

/** The counters a run reports while the worker walks its packages. */
export type RunProgressCounters = {
  readonly packagesTotal: number;
  readonly packagesProcessed: number;
};

/**
 * Progress as a whole percentage, for the progress bar.
 *
 * Returns `null` when the total is not yet known (a PENDING run has not been
 * planned, so its total is zero) — the caller shows an indeterminate bar rather
 * than a misleading 0% or 100%.
 *
 * @param counters The run's package counters.
 * @returns A percentage clamped to 0-100, or `null` when it cannot be computed.
 */
export const runProgressPercent = (
  counters: RunProgressCounters,
): number | null => {
  if (counters.packagesTotal <= 0) {
    return null;
  }
  const ratio = counters.packagesProcessed / counters.packagesTotal;
  return Math.min(100, Math.max(0, Math.round(ratio * 100)));
};
